import { NgModule, ModuleWithProviders } from '@angular/core';
import { NgxIndexedDBModule, DBConfig } from 'ngx-indexed-db';

import { CacheManagerService } from './services/cache-manager.service';
import { WebSocketManagerService } from './services/websocket-manager.service';
import { CacheRevalidationService } from './services/cache-revalidation.service';
import { CacheStatusService } from './services/cache-status.service';
import { CacheEventBus } from './services/cache-event-bus.service';
import { DataApiService } from './services/data-api.service';

/**
 * Configuration interface for CacheModule.
 */
export interface CacheModuleConfig {
  version: string;
}

@NgModule({
  providers: [
    CacheManagerService,
    WebSocketManagerService,
    CacheRevalidationService,
    CacheStatusService,
    CacheEventBus,
    DataApiService
  ]
})
export class CacheModule {
  /**
   * Use this method in your root module to provide the CacheModule with configuration.
   *
   * @param config Configuration containing version from package.json.
   * @returns ModuleWithProviders of the CacheModule.
   */
  public static forRoot(config: CacheModuleConfig): ModuleWithProviders<CacheModule> {
    const dbConfig = this.generateIndexedDBOptions(config);

    return {
      ngModule: CacheModule,
      providers: [
        CacheManagerService,
        WebSocketManagerService,
        CacheRevalidationService,
        CacheStatusService,
        CacheEventBus,
        // Provide the IndexedDB configuration
        ...NgxIndexedDBModule.forRoot(dbConfig).providers || []
      ]
    };
  }

  /**
   * Create database configuration with the provided version.
   *
   * @param config Configuration containing version.
   * @returns Database configuration.
   */
  private static generateIndexedDBOptions(config: CacheModuleConfig): DBConfig {
    return {
      name: 'newroomConnectCache',
      version: this.generateDbVersion(config.version),
      objectStoresMeta: [
        {
          store: 'areas',
          storeConfig: { keyPath: 'id', autoIncrement: false },
          storeSchema: [
            { name: 'projectId', keypath: 'projectId', options: { unique: false } },
            { name: 'slug', keypath: 'slug', options: { unique: true } },
            { name: 'timestamp', keypath: 'timestamp', options: { unique: false } },
            { name: 'userId', keypath: 'userId', options: { unique: false } }
          ]
        }
      ]
    };
  }

  /**
   * Generate database version from package.json version, ignoring patch version.
   * Uses major.minor with padding to allow up to 99 minor versions per major.
   *
   * @param version The version string from package.json.
   *
   * @returns Database version number based on major.minor only.
   *
   * @example
   * "2.7.0" -> 207
   * "2.7.1" -> 207 (same as 2.7.0, no DB migration)
   * "2.10.1" -> 210
   * "10.5.3" -> 1005
   */
  private static generateDbVersion(version: string): number {
    const [major, minor] = version.split('.').map(v => parseInt(v, 10));

    return major * 100 + minor;
  }
}
